import { useEffect, useMemo, useRef, useState } from "react";
import { createFrameSchedule } from "../../core/animation/frameSchedule";
import type { AlterlyEffectInstance } from "../../core/effects/effectRegistry";
import { downloadBlob } from "../../core/export/downloadBlob";
import { exportHalftoneSvg, getVectorExportEffect } from "../../core/export/exportHalftoneSvg";
import { exportRasterImage } from "../../core/export/exportRasterImage";
import { exportVideo } from "../../core/export/exportVideo";
import {
  codecForContainer,
  detectVideoExportCapability,
  type VideoContainer,
  type VideoExportCapability,
} from "../../core/export/videoCapabilities";
import type { MediaAsset } from "../../core/media/mediaTypes";
import { createRenderedVideoFrameProvider } from "../../infrastructure/codecs/createRenderedVideoFrameProvider";
import { inspectSourceAudio, type SourceAudioInfo } from "../../infrastructure/codecs/inspectSourceAudio";
import { MediabunnyVideoMuxer } from "../../infrastructure/codecs/MediabunnyVideoMuxer";

type ExportPanelProps = {
  asset: MediaAsset | null;
  effects: readonly AlterlyEffectInstance[];
};

type BusyKind = "png" | "svg" | "video" | null;
type CapabilityResult = {
  key: string;
  value: VideoExportCapability;
};
type AudioResult = {
  key: string;
  value: SourceAudioInfo;
};

function baseFilename(name: string) {
  const trimmed = name.replace(/\.[^.]+$/, "").trim();
  return trimmed || "alterly-export";
}

export function ExportPanel({ asset, effects }: ExportPanelProps) {
  const [busy, setBusy] = useState<BusyKind>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoContainer, setVideoContainer] = useState<VideoContainer>("mp4");
  const [fps, setFps] = useState(30);
  const [bitrateMbps, setBitrateMbps] = useState(8);
  const [videoProgress, setVideoProgress] = useState(0);
  const [preserveAudio, setPreserveAudio] = useState(true);
  const [capabilityResult, setCapabilityResult] = useState<CapabilityResult | null>(null);
  const [audioResult, setAudioResult] = useState<AudioResult | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const vectorEffect = useMemo(() => getVectorExportEffect(effects), [effects]);
  const canExportStill = asset?.kind === "image";
  const canExportVideo = asset?.kind === "video" && typeof asset.duration === "number" && asset.duration > 0;
  const capabilityKey = asset?.kind === "video"
    ? `${asset.id}:${videoContainer}:${fps}:${bitrateMbps}:${asset.dimensions.width}x${asset.dimensions.height}`
    : "none";
  const audioKey = asset?.kind === "video" ? `${asset.id}:${videoContainer}` : "none";
  const videoCapability = capabilityResult?.key === capabilityKey ? capabilityResult.value : null;
  const sourceAudio = audioResult?.key === audioKey ? audioResult.value : null;
  const checkingCapability = asset?.kind === "video" && videoCapability === null;
  const checkingAudio = asset?.kind === "video" && sourceAudio === null;
  const canPreserveAudio = sourceAudio?.hasAudio === true && sourceAudio.compatible;
  const effectivePreserveAudio = preserveAudio && canPreserveAudio;

  useEffect(() => {
    if (!asset || asset.kind !== "video" || !asset.duration) return;

    let cancelled = false;
    const requestKey = capabilityKey;
    const schedule = createFrameSchedule(asset.duration, fps);

    void detectVideoExportCapability(
      videoContainer,
      asset.dimensions,
      schedule,
      Math.round(bitrateMbps * 1_000_000),
    ).then((capability) => {
      if (cancelled) return;
      setCapabilityResult({ key: requestKey, value: capability });
    }).catch(() => {
      if (cancelled) return;
      setCapabilityResult({
        key: requestKey,
        value: {
          container: videoContainer,
          codec: codecForContainer(videoContainer),
          supported: false,
          reason: "Video export capability could not be verified in this browser.",
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [asset, bitrateMbps, capabilityKey, fps, videoContainer]);

  useEffect(() => {
    if (!asset || asset.kind !== "video") return;

    let cancelled = false;
    const requestKey = audioKey;
    void inspectSourceAudio(asset.sourceFile, videoContainer).then((value) => {
      if (!cancelled) setAudioResult({ key: requestKey, value });
    }).catch(() => {
      if (cancelled) return;
      setAudioResult({
        key: requestKey,
        value: {
          hasAudio: true,
          codec: null,
          compatible: false,
          reason: "Source audio could not be inspected for passthrough.",
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [asset, audioKey, videoContainer]);

  const exportPng = async () => {
    if (!asset || asset.kind !== "image") return;
    setBusy("png");
    setError(null);
    try {
      const result = await exportRasterImage(asset, effects, { format: "image/png", scale: 1 });
      downloadBlob(result.blob, result.filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "PNG export failed.");
    } finally {
      setBusy(null);
    }
  };

  const exportSvg = async () => {
    if (!asset || asset.kind !== "image" || !vectorEffect) return;
    setBusy("svg");
    setError(null);
    try {
      const result = await exportHalftoneSvg(asset, vectorEffect);
      downloadBlob(result.blob, result.filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "SVG export failed.");
    } finally {
      setBusy(null);
    }
  };

  const exportVideoFile = async () => {
    if (!asset || asset.kind !== "video" || !asset.duration || videoCapability?.supported !== true) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setBusy("video");
    setError(null);
    setVideoProgress(0);

    const schedule = createFrameSchedule(asset.duration, fps);
    const renderedFrames = createRenderedVideoFrameProvider(asset, effects, schedule);

    try {
      const blob = await exportVideo({
        settings: {
          codec: codecForContainer(videoContainer),
          width: asset.dimensions.width,
          height: asset.dimensions.height,
          bitrate: Math.round(bitrateMbps * 1_000_000),
          schedule,
          hardwareAcceleration: "prefer-hardware",
        },
        frameProvider: renderedFrames.frameProvider,
        muxer: new MediabunnyVideoMuxer(videoContainer, effectivePreserveAudio ? asset.sourceFile : null),
        signal: controller.signal,
        onProgress: ({ progress }) => setVideoProgress(progress),
      });

      downloadBlob(blob, `${baseFilename(asset.name)}-alterly.${videoContainer}`);
      setVideoProgress(1);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") {
        setError("Video export cancelled.");
      } else {
        setError(cause instanceof Error ? cause.message : "Video export failed.");
      }
    } finally {
      renderedFrames.dispose();
      abortControllerRef.current = null;
      setBusy(null);
    }
  };

  const cancelVideoExport = () => {
    abortControllerRef.current?.abort(new DOMException("Video export cancelled.", "AbortError"));
  };

  return (
    <section className="export-panel" aria-label="Export">
      <div className="panel-label">Export</div>

      {canExportStill && (
        <div className="export-panel__actions">
          <button type="button" disabled={busy !== null} onClick={() => void exportPng()}>
            {busy === "png" ? "Rendering…" : "PNG · Full size"}
          </button>
          <button type="button" disabled={!vectorEffect || busy !== null} onClick={() => void exportSvg()}>
            {busy === "svg" ? "Building SVG…" : "SVG · True vector"}
          </button>
        </div>
      )}

      {!vectorEffect && canExportStill && (
        <p>SVG requires exactly one enabled Halftone effect. Raster-only effects are never wrapped as fake vector.</p>
      )}

      {asset?.kind === "video" && (
        <div className="video-export">
          <div className="video-export__grid">
            <label>
              <span>Container</span>
              <select
                value={videoContainer}
                disabled={busy !== null}
                onChange={(event) => setVideoContainer(event.target.value as VideoContainer)}
              >
                <option value="mp4">MP4 · H.264</option>
                <option value="webm">WebM · VP9</option>
              </select>
            </label>
            <label>
              <span>FPS</span>
              <select value={fps} disabled={busy !== null} onChange={(event) => setFps(Number(event.target.value))}>
                <option value="24">24</option>
                <option value="30">30</option>
                <option value="60">60</option>
              </select>
            </label>
          </div>

          <label className="video-export__bitrate">
            <span>Bitrate <b>{bitrateMbps} Mbps</b></span>
            <input
              type="range"
              min="2"
              max="30"
              step="1"
              value={bitrateMbps}
              disabled={busy !== null}
              onChange={(event) => setBitrateMbps(Number(event.target.value))}
            />
          </label>

          <label className="video-export__audio">
            <span>
              <input
                type="checkbox"
                checked={effectivePreserveAudio}
                disabled={busy !== null || checkingAudio || !canPreserveAudio}
                onChange={(event) => setPreserveAudio(event.target.checked)}
              />
              Preserve source audio
            </span>
          </label>

          {checkingAudio && <p>Checking source audio…</p>}
          {!checkingAudio && sourceAudio?.hasAudio === false && <p>No audio track found in the source video.</p>}
          {!checkingAudio && sourceAudio?.hasAudio === true && sourceAudio.compatible && (
            <p>{sourceAudio.codec?.toUpperCase()} audio can be copied without re-encoding.</p>
          )}
          {!checkingAudio && sourceAudio?.hasAudio === true && !sourceAudio.compatible && (
            <p>{sourceAudio.reason} Video export remains available without audio.</p>
          )}

          {checkingCapability && <p>Checking codec support…</p>}
          {!checkingCapability && videoCapability?.supported === false && (
            <p className="export-panel__error" role="status">{videoCapability.reason}</p>
          )}

          {busy === "video" && (
            <div className="video-export__progress" aria-live="polite">
              <progress max="1" value={videoProgress} />
              <span>{Math.round(videoProgress * 100)}%</span>
            </div>
          )}

          <div className="export-panel__actions">
            <button
              type="button"
              disabled={!canExportVideo || busy !== null || checkingCapability || videoCapability?.supported !== true}
              onClick={() => void exportVideoFile()}
            >
              {busy === "video" ? "Encoding…" : `Export ${videoContainer.toUpperCase()}`}
            </button>
            {busy === "video" && (
              <button type="button" className="export-panel__secondary" onClick={cancelVideoExport}>Cancel</button>
            )}
          </div>
          <p>Frame-driven WebCodecs video with optional encoded-audio passthrough when the source codec fits the selected container.</p>
        </div>
      )}

      {error && <p className="export-panel__error" role="alert">{error}</p>}
    </section>
  );
}
